import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Link2, Users, Boxes, Trash2, Check, X, Plus, Loader2, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Shared Resources manager (Session 36, P3 item e).
 * Link partner practitioners, create shared resources (room / Zoom / device),
 * and map which session types use each resource to prevent double-booking.
 */
export default function SharedResourcesButton({ className = '' }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState({ accepted: [], incoming: [], outgoing: [] });
  const [resources, setResources] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [resourceName, setResourceName] = useState('');
  const [newResMembers, setNewResMembers] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [l, r, s] = await Promise.all([
        axios.get(`${API}/practitioner/links`, { headers }),
        axios.get(`${API}/practitioner/shared-resources`, { headers }),
        axios.get(`${API}/practitioner/session-types`, { headers }),
      ]);
      setLinks(l.data);
      setResources(r.data.resources || []);
      setPartners(r.data.partners || []);
      setSessionTypes(s.data.session_types || []);
    } catch (err) {
      // session-types is vendor-gated; ignore its failure quietly
    }
  }, []); // eslint deps intentionally minimal

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/practitioner/links/invite`, { partner_email: inviteEmail.trim() }, { headers });
      toast.success('Invite sent');
      setInviteEmail('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    } finally { setBusy(false); }
  };

  const respond = async (id, action) => {
    try {
      await axios.post(`${API}/practitioner/links/${id}/${action}`, {}, { headers });
      toast.success(action === 'accept' ? 'Linked!' : 'Declined');
      refresh();
    } catch (err) { toast.error('Action failed'); }
  };

  const removeLink = async (id) => {
    try { await axios.delete(`${API}/practitioner/links/${id}`, { headers }); refresh(); }
    catch (err) { toast.error('Remove failed'); }
  };

  const createResource = async () => {
    if (!resourceName.trim()) { toast.error('Name the resource'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/practitioner/shared-resources`, { name: resourceName.trim(), member_user_ids: newResMembers }, { headers });
      toast.success('Resource created');
      setResourceName(''); setNewResMembers([]);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const deleteResource = async (id) => {
    if (!window.confirm('Delete this shared resource?')) return;
    try { await axios.delete(`${API}/practitioner/shared-resources/${id}`, { headers }); toast.success('Deleted'); refresh(); }
    catch (err) { toast.error('Delete failed'); }
  };

  const toggleMapping = async (st, resourceId) => {
    const current = st.shared_resource_ids || [];
    const next = current.includes(resourceId) ? current.filter((x) => x !== resourceId) : [...current, resourceId];
    try {
      await axios.post(`${API}/practitioner/session-types/${st.id}/resources`, { resource_ids: next }, { headers });
      setSessionTypes((prev) => prev.map((x) => x.id === st.id ? { ...x, shared_resource_ids: next } : x));
    } catch (err) { toast.error('Update failed'); }
  };

  const toggleNewMember = (uid) =>
    setNewResMembers((m) => m.includes(uid) ? m.filter((x) => x !== uid) : [...m, uid]);

  return (
    <>
      <Button variant="outline" className={`text-white border-white/10 hover:bg-white/5 ${className}`} onClick={() => setOpen(true)} data-testid="open-shared-resources-btn">
        <Boxes className="w-4 h-4 mr-2" /> Shared Resources
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="shared-resources-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Boxes className="w-5 h-5 text-purple-600" /> Shared Resources</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="links" className="mt-2">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="links" data-testid="tab-links"><Link2 className="w-4 h-4 mr-1" /> Partners</TabsTrigger>
              <TabsTrigger value="resources" data-testid="tab-resources"><Boxes className="w-4 h-4 mr-1" /> Resources</TabsTrigger>
              <TabsTrigger value="mapping" data-testid="tab-mapping"><Users className="w-4 h-4 mr-1" /> Sessions</TabsTrigger>
            </TabsList>

            {/* Partner links */}
            <TabsContent value="links" className="space-y-4 pt-3">
              <p className="text-sm text-gray-500">Link a partner practitioner who shares a space or technology with you. Both of you must accept.</p>
              <div className="flex gap-2">
                <Input placeholder="partner@email.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" />
                <Button onClick={invite} disabled={busy} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="send-invite-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                </Button>
              </div>

              {links.incoming.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-600 mb-1">Invites for you</h5>
                  {links.incoming.map((l) => (
                    <div key={l.id} className="flex items-center justify-between py-2 border-b" data-testid={`incoming-${l.id}`}>
                      <span className="text-sm">{l.partner_name} <span className="text-gray-400">({l.partner_email})</span></span>
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2" onClick={() => respond(l.id, 'accept')} data-testid={`accept-${l.id}`}><Check className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => respond(l.id, 'decline')} data-testid={`decline-${l.id}`}><X className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {links.outgoing.length > 0 && (
                <div>
                  <h5 className="text-xs font-semibold text-gray-600 mb-1">Pending (sent)</h5>
                  {links.outgoing.map((l) => (
                    <div key={l.id} className="flex items-center justify-between py-2 border-b text-sm text-gray-500">
                      <span>{l.partner_email}</span><span className="text-xs italic">awaiting</span>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <h5 className="text-xs font-semibold text-gray-600 mb-1">Linked partners</h5>
                {links.accepted.length === 0 ? <p className="text-sm text-gray-400">No linked partners yet.</p> :
                  links.accepted.map((l) => (
                    <div key={l.id} className="flex items-center justify-between py-2 border-b" data-testid={`partner-${l.id}`}>
                      <span className="text-sm">{l.partner_name}</span>
                      <button onClick={() => removeLink(l.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
              </div>
            </TabsContent>

            {/* Resources */}
            <TabsContent value="resources" className="space-y-4 pt-3">
              <p className="text-sm text-gray-500">A shared resource is a room, Zoom account, or device that linked partners share.</p>
              <div className="space-y-2 p-3 rounded-lg bg-gray-50 border">
                <Input placeholder="e.g. Healing Room A, Zoom Account" value={resourceName} onChange={(e) => setResourceName(e.target.value)} data-testid="resource-name-input" />
                {partners.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Share with:</p>
                    <div className="flex flex-wrap gap-2">
                      {partners.map((p) => (
                        <label key={p.user_id} className={`text-xs px-2 py-1 rounded-full cursor-pointer border ${newResMembers.includes(p.user_id) ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600'}`}>
                          <input type="checkbox" className="hidden" checked={newResMembers.includes(p.user_id)} onChange={() => toggleNewMember(p.user_id)} />
                          {p.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <Button size="sm" onClick={createResource} disabled={busy} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-resource-btn">
                  <Plus className="w-3 h-3 mr-1" /> Create Resource
                </Button>
              </div>
              {resources.length === 0 ? <p className="text-sm text-gray-400">No shared resources yet.</p> :
                resources.map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-2 border-b" data-testid={`resource-${r.id}`}>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{r.name}</p>
                      <p className="text-xs text-gray-400">{(r.members || []).map((m) => m.name).join(', ')}</p>
                    </div>
                    {r.owner_user_id && <button onClick={() => deleteResource(r.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                ))}
            </TabsContent>

            {/* Session-type mapping */}
            <TabsContent value="mapping" className="space-y-4 pt-3">
              <p className="text-sm text-gray-500">Tag which of your session types use each shared resource. Only tagged sessions block each other.</p>
              {sessionTypes.length === 0 ? <p className="text-sm text-gray-400">No session types found.</p> :
                sessionTypes.map((st) => (
                  <div key={st.id} className="py-2 border-b" data-testid={`map-st-${st.id}`}>
                    <p className="text-sm font-medium text-gray-800 mb-1">{st.name}</p>
                    {resources.length === 0 ? <p className="text-xs text-gray-400">Create a resource first.</p> :
                      <div className="flex flex-wrap gap-2">
                        {resources.map((r) => (
                          <label key={r.id} className={`text-xs px-2 py-1 rounded-full cursor-pointer border ${(st.shared_resource_ids || []).includes(r.id) ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600'}`} data-testid={`map-${st.id}-${r.id}`}>
                            <input type="checkbox" className="hidden" checked={(st.shared_resource_ids || []).includes(r.id)} onChange={() => toggleMapping(st, r.id)} />
                            {r.name}
                          </label>
                        ))}
                      </div>}
                  </div>
                ))}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
